import React, { useState, useEffect } from 'react';
import { getClinicalTerms, createClinicalTerm, updateClinicalTerm, deleteClinicalTerm } from '../../servicios/apiClinica';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ClinicalTerms = () => {
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);
    const [searchFilter, setSearchFilter] = useState('');
    
    // States for Modal and CRUD
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [formData, setFormData] = useState({ category: 'procedure', code: '', description: '', how_its_done: '' });

    useEffect(() => {
        loadTerms();
    }, []);

    const loadTerms = async () => {
        setLoading(true);
        try {
            const data = await getClinicalTerms(); // Sin categoría = trae todos
            setTerms(data);
        } catch (error) {
            console.error(error);
            setMessage({ type: 'danger', text: 'Error al cargar términos.' });
        }
        setLoading(false);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setMessage(null);

        const formDataFile = new FormData();
        formDataFile.append('file', file);
        formDataFile.append('category', 'procedure'); // default category for import

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/clinical-records/clinical-terms/import_excel/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataFile
            });

            const result = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: result.message || 'Términos importados correctamente.' });
                loadTerms();
            } else {
                setMessage({ type: 'danger', text: result.error || 'Error al importar.' });
            }
        } catch (error) {
            console.error(error);
            setMessage({ type: 'danger', text: 'Error de conexión.' });
        }
        setUploading(false);
        e.target.value = ''; // Reset input
    };

    const handleOpenModal = (mode, termData = null) => {
        setModalMode(mode);
        setSelectedTerm(termData);
        setFormData(termData ? { ...termData } : { category: 'procedure', code: '', description: '', how_its_done: '' });
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (modalMode === 'create') {
                await createClinicalTerm({...formData, category: 'procedure'});
                setMessage({ type: 'success', text: 'Término creado correctamente.' });
            } else {
                await updateClinicalTerm(selectedTerm.id, {...formData});
                setMessage({ type: 'success', text: 'Término actualizado correctamente.' });
            }
            setShowModal(false);
            loadTerms();
        } catch (error) {
            setMessage({ type: 'danger', text: 'Error al guardar el término.' });
        }
    };

    const handleDelete = async (id, description) => {
        if (window.confirm(`¿Estás seguro de eliminar el término "${description}"?`)) {
            try {
                await deleteClinicalTerm(id);
                setMessage({ type: 'success', text: 'Término eliminado correctamente.' });
                loadTerms();
            } catch (error) {
                setMessage({ type: 'danger', text: 'Error al eliminar el término.' });
            }
        }
    };

    // Filtrar términos por búsqueda
    const filteredTerms = terms.filter(t => {
        if (!searchFilter.trim()) return true;
        const search = searchFilter.toLowerCase();
        return (t.code || '').toLowerCase().includes(search) || (t.description || '').toLowerCase().includes(search);
    });

    return (
        <div className="container-fluid p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex flex-column">
                    <h2 style={{ color: '#111827', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '4px' }}>
                        <i className="bi bi-book-half me-2" style={{ color: '#111827' }}></i> 
                        Diccionario Clínico
                    </h2>
                    <p className="text-muted mb-0" style={{ fontSize: '1rem', fontWeight: '500' }}>
                        Gestiona términos de autocompletado para la historia clínica. Un solo diccionario unificado.
                    </p>
                </div>
                
                <div className="d-flex gap-2 align-items-center">
                    <label htmlFor="file-upload" className="btn btn-outline-primary d-flex align-items-center" style={{ borderRadius: '8px', fontWeight: 'bold' }}>
                        {uploading ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : (
                            <i className="bi bi-file-earmark-excel-fill me-2"></i>
                        )}
                        Subir Excel
                    </label>
                    <input 
                        id="file-upload"
                        type="file" 
                        accept=".xlsx, .xls" 
                        className="d-none" 
                        onChange={handleFileUpload}
                        disabled={uploading}
                    />
                    <button 
                        onClick={() => handleOpenModal('create')}
                        className="btn shadow-sm" 
                        style={{ backgroundColor: '#111827', color: 'white', fontWeight: 'bold', borderRadius: '8px' }}
                    >
                        <i className="bi bi-plus-lg me-2"></i> Nuevo Término
                    </button>
                </div>
            </div>
            
            {message && (
                <div className={`alert alert-${message.type} alert-dismissible fade show shadow-sm`} role="alert" style={{ borderRadius: '10px' }}>
                    <i className={`bi ${message.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label="Close"></button>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold m-0" style={{ color: '#333' }}>
                    <i className="bi bi-list-ul me-2 text-primary"></i> 
                    Todos los Términos
                </h4>
                <div className="d-flex align-items-center gap-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder=" Buscar término..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        style={{ minWidth: '250px', borderRadius: '8px' }}
                    />
                    <span className="badge bg-primary rounded-pill px-3 py-2" style={{ fontSize: '1rem' }}>
                        Total: {filteredTerms.length}
                    </span>
                </div>
            </div>
            
            <div className="card shadow-sm border-0" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}></div>
                            <p className="mt-3 text-muted fw-bold">Cargando diccionario clínico...</p>
                        </div>
                    ) : (
                        <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                            <table className="table table-hover mb-0">
                                <thead style={{ backgroundColor: '#111827', color: 'white', position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th className="py-3 px-4 border-0 text-center" style={{ width: '20%' }}>Nombre del Término</th>
                                        <th className="py-3 px-4 border-0" style={{ width: '35%' }}>Descripción (En qué consiste)</th>
                                        <th className="py-3 px-4 border-0" style={{ width: '30%' }}>Cómo se realiza</th>
                                        <th className="py-3 px-4 border-0 text-center" style={{ width: '15%' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTerms.length > 0 ? (
                                        filteredTerms.map(t => (
                                            <tr key={t.id} style={{ transition: 'background-color 0.2s' }}>
                                                <td className="py-3 px-4 text-center">
                                                    {t.code ? (
                                                        <span className="badge" style={{ backgroundColor: '#f3f4f6', color: '#111827', border: '1px solid #e5e7eb', fontSize: '0.9rem', padding: '6px 12px' }}>
                                                            {t.code}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 align-middle" style={{ fontWeight: '500', color: '#555' }}>
                                                    {t.description}
                                                </td>
                                                <td className="py-3 px-4 align-middle" style={{ fontWeight: '400', color: '#777', fontSize: '0.9rem' }}>
                                                    {t.how_its_done || <span className="text-muted fst-italic">—</span>}
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    <button onClick={() => handleOpenModal('edit', t)} className="btn btn-sm btn-outline-warning rounded-circle me-2" title="Editar">
                                                        <i className="bi bi-pencil-fill"></i>
                                                    </button>
                                                    <button onClick={() => handleDelete(t.id, t.description)} className="btn btn-sm btn-outline-danger rounded-circle" title="Eliminar">
                                                        <i className="bi bi-trash-fill"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center py-5 text-muted">
                                                <i className="bi bi-inbox fs-1 d-block mb-3 opacity-50"></i>
                                                <h5>No hay términos cargados</h5>
                                                <p>Utiliza la opción de importar archivo Excel arriba o crea términos manualmente.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal CRUD */}
            {showModal && (
                <div 
                    className="modal show d-block" 
                    style={{ 
                        position: 'fixed', 
                        top: 0, 
                        left: 0, 
                        width: '100vw', 
                        height: '100vh', 
                        backgroundColor: 'rgba(0,0,0,0.6)', 
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: 0,
                        padding: 0
                    }}
                >
                    <div className="modal-dialog w-100" style={{ maxWidth: '600px', margin: '1.75rem auto' }}>
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header border-0 p-4" style={{ backgroundColor: '#111827' }}>
                                <div className="w-100">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <h4 className="modal-title fw-bold m-0" style={{ color: '#ffffff', letterSpacing: '0.5px' }}>
                                            <i className={`bi ${modalMode === 'create' ? 'bi-plus-circle' : 'bi-pencil'} me-2`}></i>
                                            {modalMode === 'create' ? 'Nuevo Término Clínico' : 'Editar Término Clínico'}
                                        </h4>
                                        <button type="button" onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', color: '#ffffff', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                                    </div>
                                    <span style={{ color: '#e0e0e0', fontSize: '0.95rem' }}>Complete la información del término clínico.</span>
                                </div>
                            </div>
                            
                            <div className="modal-body p-4 bg-light">
                                <form id="term-form" onSubmit={handleSave}>
                                    <div className="card border-0 shadow-sm" style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}>
                                        <div className="card-body p-4">
                                            <div className="mb-4">
                                                <label className="fw-bold text-dark mb-2" style={{ fontSize: '0.95rem' }}>Nombre corto o Código</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control form-control-lg" 
                                                    value={formData.code}
                                                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                                                    placeholder="Ej. Operativa simple / A01"
                                                    style={{ fontSize: '1rem' }}
                                                />
                                            </div>
                                            <div>
                                                <label className="fw-bold text-dark mb-2" style={{ fontSize: '0.95rem' }}>Descripción / En qué consiste <span className="text-danger">*</span></label>
                                                <textarea 
                                                    className="form-control" 
                                                    rows="3"
                                                    value={formData.description}
                                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                                    required
                                                    placeholder="Descripción detallada del término..."
                                                    style={{ resize: 'none', fontSize: '1rem' }}
                                                ></textarea>
                                            </div>
                                            <div className="mt-4">
                                                <label className="fw-bold text-dark mb-2" style={{ fontSize: '0.95rem' }}>Cómo se realiza <span className="text-muted">(Opcional)</span></label>
                                                <textarea 
                                                    className="form-control" 
                                                    rows="3"
                                                    value={formData.how_its_done || ''}
                                                    onChange={(e) => setFormData({...formData, how_its_done: e.target.value})}
                                                    placeholder="Describa cómo se realiza este procedimiento..."
                                                    style={{ resize: 'none', fontSize: '1rem' }}
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="modal-footer bg-light border-0 p-4 pt-0 d-flex justify-content-end gap-3">
                                <button type="button" className="btn btn-outline-secondary fw-bold px-4 py-2" onClick={() => setShowModal(false)} style={{ borderRadius: '8px' }}>
                                    Cancelar
                                </button>
                                <button type="submit" form="term-form" className="btn fw-bold px-4 py-2" style={{ backgroundColor: '#111827', color: 'white', borderRadius: '8px' }}>
                                    <i className="bi bi-save-fill me-2"></i>Guardar Cambios
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClinicalTerms;
